import React, { useState } from 'react';
import { Download, BookOpen, ChevronDown, Loader2, Calendar, Users, Bell, MessageSquare, Clock, EyeOff, UserPlus, CalendarPlus, Edit, Mail } from 'lucide-react';
import { generateUserGuidePDF } from '../components/UserGuidePDF';

const UserGuidePage = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownload = async () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    try {
      await generateUserGuidePDF();
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // Guide sections data
  const sections = [
    {
      id: 1,
      icon: Calendar,
      title: 'Creating a Fixed Calendar',
      color: 'bg-blue-500',
      summary: 'Set up hourly time slots for 60-minute appointments',
      content: {
        intro: 'A Fixed Calendar uses hourly time slots where appointments start at the top of each hour (9:00, 10:00, 11:00). Ideal for standard 60-minute services.',
        steps: [
          'Go to Dashboard → My Calendars',
          'Click "+ Add Calendar"',
          'Fill in calendar details',
          'Select "Fixed (Top-of-Hour)" as Booking Type',
          'Click "Create Calendar"'
        ],
        fields: [
          { name: 'Calendar Name', desc: 'Unique name (cannot change later)', example: 'Pickleball Coaching' },
          { name: 'Type', desc: 'Category of services', example: 'Sports Coaching' },
          { name: 'Location', desc: 'Physical address or virtual', example: '123 Main St' },
          { name: 'Booking Type', desc: 'Fixed = hourly slots', example: 'Fixed (Top-of-Hour)' },
          { name: 'Min Advance Booking', desc: 'Hours notice required', example: '24 hours' },
          { name: 'Max Advance Booking', desc: 'Days ahead to book', example: '14 days' },
        ],
        tip: 'Fixed calendars require all services to have exactly 60-minute duration.'
      }
    },
    {
      id: 2,
      icon: CalendarPlus,
      title: 'Adding Services & Calendar Association',
      color: 'bg-green-500',
      summary: 'Create services and link them to your calendar',
      content: {
        intro: 'Services define what customers can book. Associate services with calendars in three ways.',
        methods: [
          {
            name: 'Method A: During Service Creation',
            steps: ['Go to Dashboard → My Services', 'Click "+ Add Service"', 'Fill details and select calendar(s)', 'Click "Add Service"']
          },
          {
            name: 'Method B: Edit Existing Service',
            steps: ['Go to My Services', 'Click Edit icon', 'Check calendar(s) in "Add to Calendar(s)"', 'Click "Update Service"']
          },
          {
            name: 'Method C: From Calendar Settings',
            steps: ['Go to My Calendars', 'Click Edit icon', 'Go to "Calendar Services" tab', 'Check/uncheck services', 'Click "Save Changes"']
          }
        ],
        fields: [
          { name: 'Service Name', desc: 'Name shown to customers', example: 'One-on-One Lesson' },
          { name: 'Price', desc: 'Cost of service', example: 'HK$500' },
          { name: 'Duration', desc: 'Must be 60 min for Fixed', example: '60 minutes' },
          { name: 'Max Customers', desc: 'Slots per booking', example: '1 (private) or 2-3 (group)' },
        ],
        tip: 'For group lessons, set Max Customers to 2 or 3 to allow multiple bookings per slot.'
      }
    },
    {
      id: 3,
      icon: Clock,
      title: 'Setting Availability',
      color: 'bg-purple-500',
      summary: 'Define when you are available for bookings',
      content: {
        intro: 'Without setting availability, customers cannot book. Set single-day or recurring availability.',
        steps: [
          'Open your calendar (Calendar icon in header)',
          'Navigate to desired date',
          'Right-click on any time slot',
          'Select "Set Availability"',
          'Configure start/end times',
          'Click "Set Availability"'
        ],
        recurring: {
          title: 'For Recurring Availability:',
          steps: ['Check "Repeat Availability"', 'Select days (Mon, Wed, Fri, etc.)', 'Choose frequency (Weekly, Every 2 weeks)', 'Choose duration (One Month, Forever, etc.)']
        },
        colors: [
          { color: 'bg-green-500', name: 'Green', meaning: 'Available for booking' },
          { color: 'bg-yellow-500', name: 'Yellow', meaning: 'On Hold (hidden from customers)' },
          { color: 'bg-blue-500', name: 'Blue', meaning: 'Booked' },
          { color: 'bg-gray-400', name: 'Gray', meaning: 'Past (not bookable)' },
        ],
        tip: 'Use recurring availability to set up your schedule for months in advance.'
      }
    },
    {
      id: 4,
      icon: EyeOff,
      title: 'Hide/Release Available Slots',
      color: 'bg-yellow-500',
      summary: 'Temporarily hide slots without deleting availability',
      content: {
        intro: 'The "On Hold" feature lets you hide slots from customers for planning, VIP reservations, or gradual release.',
        hold: {
          title: 'To Put a Slot On Hold:',
          steps: ['Find an Available (green) slot', 'Right-click on the slot', 'Select "Hold This Slot"', 'Slot turns yellow (hidden from customers)']
        },
        release: {
          title: 'To Release a Held Slot:',
          steps: ['Find a Held (yellow) slot', 'Right-click on the slot', 'Select "Release Slot"', 'Choose notification options', 'Click "Release Slot"']
        },
        useCases: [
          'Planning: Set up future weeks while keeping them hidden',
          'VIP Bookings: Reserve slots for specific customers',
          'Maintenance: Block time for breaks or setup',
          'Gradual Release: Release slots in batches'
        ],
        tip: 'When releasing multiple slots, send one batch notification instead of individual ones.'
      }
    },
    {
      id: 5,
      icon: UserPlus,
      title: 'Adding a Customer',
      color: 'bg-pink-500',
      summary: 'Add customers to your calendar for bookings',
      content: {
        intro: 'Each calendar has its own customer list. Add customers to enable booking for them.',
        steps: [
          'Go to Dashboard → Manage Customers',
          'Select your calendar (if multiple)',
          'Click "+ Add Customer"',
          'Fill in customer details',
          'Click "Create"'
        ],
        fields: [
          { name: 'Full Name', desc: 'Display name', required: true },
          { name: 'Email', desc: 'For login and notifications', required: true },
          { name: 'Phone', desc: 'For SMS notifications', required: false },
          { name: 'Password', desc: 'For customer portal (min 6 chars)', required: true },
          { name: 'SMS Consent', desc: 'Enable SMS reminders', required: false },
        ],
        actions: [
          { icon: '💬', name: 'Message', desc: 'Open message console' },
          { icon: '👤', name: 'Run As', desc: 'Preview as customer' },
          { icon: '✏️', name: 'Edit', desc: 'Update details' },
          { icon: '🗑️', name: 'Delete', desc: 'Remove customer' },
        ],
        tip: 'Customers can log into their portal to self-book and manage appointments.'
      }
    },
    {
      id: 6,
      icon: CalendarPlus,
      title: 'Making a Booking for a Customer',
      color: 'bg-indigo-500',
      summary: 'Create appointments on behalf of customers',
      content: {
        intro: 'As an admin, create bookings directly from the calendar view.',
        steps: [
          'Open your calendar (Week or Day view)',
          'Find an Available (green) slot',
          'Left-click on the slot',
          'Select a Service',
          'Select a Customer (or multiple for group)',
          'Add optional notes',
          'Click "Create Appointment"'
        ],
        multiCustomer: {
          title: 'For Group Bookings:',
          points: ['If Max Customers > 1, you\'ll see checkboxes', 'Select multiple customers', 'Each receives their own confirmation']
        },
        afterBooking: [
          'Slot changes from green to blue',
          'Customer receives confirmation email with ICS file',
          'Admin receives a copy',
          'Booking appears on both calendars'
        ],
        tip: 'For group lessons, each customer gets their own appointment record and can cancel independently.'
      }
    },
    {
      id: 7,
      icon: Edit,
      title: 'Editing and Canceling Bookings',
      color: 'bg-orange-500',
      summary: 'Modify or cancel existing appointments',
      content: {
        intro: 'Edit bookings to change details or add/remove customers. Cancel when needed.',
        edit: {
          title: 'To Edit a Booking:',
          steps: ['Click on the booked (blue) slot', 'Modify service, date/time, notes, or customers', 'Click "Update Appointment"']
        },
        cancel: {
          title: 'To Cancel a Booking:',
          methods: [
            { name: 'From Edit Modal', steps: ['Click on booking', 'Click "Cancel Appointment" (red button)', 'Confirm'] },
            { name: 'From Context Menu', steps: ['Right-click on booking', 'Select "Cancel Appointment"', 'Confirm'] }
          ]
        },
        multiCustomer: [
          'Uncheck a customer to remove them (gets cancellation notice)',
          'Check new customers to add them (only new ones notified)',
          'Existing customers are NOT re-notified when adding others'
        ],
        tip: 'Cancelled slots automatically return to Available (green) status.'
      }
    },
    {
      id: 8,
      icon: Bell,
      title: 'Booking Confirmation & Reminder Emails',
      color: 'bg-red-500',
      summary: 'Automatic email notifications for bookings',
      content: {
        intro: 'yiBooking sends automatic notifications throughout the booking lifecycle.',
        emailTypes: [
          { type: 'Confirmation', when: 'Immediately after booking', content: 'Date, time, service, ICS file' },
          { type: 'Reminder', when: '24 hours before (configurable)', content: 'Same + reminder text' },
          { type: 'Modification', when: 'When booking changes', content: 'Updated details' },
          { type: 'Cancellation', when: 'When cancelled', content: 'Cancellation confirmation' },
        ],
        variables: [
          { var: '&customer-name', desc: 'Customer\'s full name' },
          { var: '&service-name', desc: 'Service name' },
          { var: '&appointment-date', desc: 'Appointment date' },
          { var: '&appointment-time', desc: 'Appointment time' },
          { var: '&booked-by', desc: 'Who made booking (Admin/Customer)' },
          { var: '&co-participants', desc: 'Other customers in group booking' },
        ],
        tip: 'Customize templates in Dashboard → Branding & Email.'
      }
    },
    {
      id: 9,
      icon: MessageSquare,
      title: 'Sending Messages to Customers',
      color: 'bg-teal-500',
      summary: 'Communicate via in-app chat, email, or SMS',
      content: {
        intro: 'Use the unified Message Console to communicate across multiple channels.',
        openMethods: [
          'From Manage Customers: Click Message icon',
          'From Calendar: Click booking → "Send Message to Customer"',
          'From Context Menu: Right-click booking → "Send Message"'
        ],
        channels: [
          { name: 'yiBooking', desc: 'In-app real-time messaging', req: 'Customer must log in' },
          { name: 'Email', desc: 'Standard email', req: 'Valid email address' },
          { name: 'SMS', desc: 'Text message', req: 'Phone number + SMS consent' },
        ],
        steps: [
          'Open Message Console for a customer',
          'Select channel tab (yiBooking, Email, SMS)',
          'For Email: Enter subject line',
          'Type your message',
          'Click "Send"'
        ],
        tip: 'Recent email subjects are saved for quick reuse. SMS limited to 160 characters.'
      }
    },
    {
      id: 10,
      icon: Mail,
      title: 'Reading Inbound Messages',
      color: 'bg-cyan-500',
      summary: 'View customer replies and conversations',
      content: {
        intro: 'The Message Console shows a unified thread of all communications with a customer.',
        features: [
          'Your messages appear on the right (outbound)',
          'Customer messages appear on the left (inbound)',
          'Channel icons show how each message was sent',
          'Timestamps on every message',
          'Read receipts for yiBooking messages',
          'Online indicator (green dot) shows if customer is online'
        ],
        emailHandling: [
          'Customer email replies are fetched from your IMAP inbox',
          'Click Refresh to manually check for new emails',
          'Long email threads are collapsible'
        ],
        bellIcon: 'The bell icon in header shows unread count and recent messages across all customers.',
        tip: 'Configure email fetch frequency in Message Console settings (1, 5, or 15 minutes).'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-yellow-500" />
              <h1 className="text-lg font-semibold text-gray-800">
                Fixed Calendar Admin Guide
              </h1>
            </div>
            
            <button
              onClick={handleDownload}
              disabled={isGeneratingPdf}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
              data-testid="download-button"
            >
              {isGeneratingPdf ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">{isGeneratingPdf ? 'Generating...' : 'Download PDF'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Intro Card */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 mb-8 border border-yellow-200">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Quick Reference Guide</h2>
          <p className="text-gray-600">
            Click any topic below to expand detailed instructions. Use the <strong>Download PDF</strong> button for a printable version.
          </p>
        </div>

        {/* Topic Cards */}
        <div className="space-y-3">
          {sections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSection === section.id;
            
            return (
              <div 
                key={section.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Topic Header - Always Visible */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
                  data-testid={`section-${section.id}-toggle`}
                >
                  <div className={`${section.color} p-2.5 rounded-lg flex-shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-800">
                      {section.id}. {section.title}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">{section.summary}</p>
                  </div>
                  <div className={`p-1 rounded-full transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-gray-100">
                    <div className="pt-4 space-y-4">
                      {/* Introduction */}
                      <p className="text-gray-600 leading-relaxed">{section.content.intro}</p>

                      {/* Steps */}
                      {section.content.steps && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-700 mb-3">Steps:</h4>
                          <ol className="space-y-2">
                            {section.content.steps.map((step, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <span className="flex-shrink-0 w-6 h-6 bg-yellow-500 text-white text-sm font-medium rounded-full flex items-center justify-center">
                                  {i + 1}
                                </span>
                                <span className="text-gray-600 pt-0.5">{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {/* Methods (for section 2) */}
                      {section.content.methods && (
                        <div className="space-y-3">
                          {section.content.methods.map((method, i) => (
                            <div key={i} className="bg-gray-50 rounded-lg p-4">
                              <h4 className="font-medium text-gray-700 mb-2">{method.name}</h4>
                              <ol className="space-y-1 text-sm">
                                {method.steps.map((step, j) => (
                                  <li key={j} className="text-gray-600 flex items-center gap-2">
                                    <span className="text-yellow-500 font-medium">{j + 1}.</span> {step}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Hold/Release (for section 4) */}
                      {section.content.hold && (
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                            <h4 className="font-medium text-yellow-800 mb-2">{section.content.hold.title}</h4>
                            <ol className="space-y-1 text-sm">
                              {section.content.hold.steps.map((step, i) => (
                                <li key={i} className="text-yellow-700">{i + 1}. {step}</li>
                              ))}
                            </ol>
                          </div>
                          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                            <h4 className="font-medium text-green-800 mb-2">{section.content.release.title}</h4>
                            <ol className="space-y-1 text-sm">
                              {section.content.release.steps.map((step, i) => (
                                <li key={i} className="text-green-700">{i + 1}. {step}</li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      )}

                      {/* Use Cases */}
                      {section.content.useCases && (
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <h4 className="font-medium text-blue-800 mb-2">Common Use Cases:</h4>
                          <ul className="space-y-1 text-sm">
                            {section.content.useCases.map((useCase, i) => (
                              <li key={i} className="text-blue-700 flex items-start gap-2">
                                <span className="text-blue-400">•</span> {useCase}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Edit/Cancel (for section 7) */}
                      {section.content.edit && (
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <h4 className="font-medium text-blue-800 mb-2">{section.content.edit.title}</h4>
                            <ol className="space-y-1 text-sm">
                              {section.content.edit.steps.map((step, i) => (
                                <li key={i} className="text-blue-700">{i + 1}. {step}</li>
                              ))}
                            </ol>
                          </div>
                          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                            <h4 className="font-medium text-red-800 mb-2">{section.content.cancel.title}</h4>
                            {section.content.cancel.methods.map((method, i) => (
                              <div key={i} className="mb-2">
                                <p className="text-sm font-medium text-red-700">{method.name}:</p>
                                <p className="text-sm text-red-600">{method.steps.join(' → ')}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recurring (for section 3) */}
                      {section.content.recurring && (
                        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                          <h4 className="font-medium text-purple-800 mb-2">{section.content.recurring.title}</h4>
                          <ol className="space-y-1 text-sm">
                            {section.content.recurring.steps.map((step, i) => (
                              <li key={i} className="text-purple-700">{i + 1}. {step}</li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {/* Slot Colors (for section 3) */}
                      {section.content.colors && (
                        <div className="flex flex-wrap gap-3">
                          {section.content.colors.map((item, i) => (
                            <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5">
                              <span className={`w-3 h-3 rounded-full ${item.color}`}></span>
                              <span className="text-sm text-gray-600"><strong>{item.name}:</strong> {item.meaning}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Fields Table */}
                      {section.content.fields && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="text-left px-3 py-2 font-medium text-gray-700 rounded-tl-lg">Field</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-700">Description</th>
                                {section.content.fields[0].example && (
                                  <th className="text-left px-3 py-2 font-medium text-gray-700 rounded-tr-lg">Example</th>
                                )}
                                {section.content.fields[0].required !== undefined && (
                                  <th className="text-left px-3 py-2 font-medium text-gray-700 rounded-tr-lg">Required</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {section.content.fields.map((field, i) => (
                                <tr key={i} className="border-b border-gray-100">
                                  <td className="px-3 py-2 font-medium text-gray-800">{field.name}</td>
                                  <td className="px-3 py-2 text-gray-600">{field.desc}</td>
                                  {field.example && <td className="px-3 py-2 text-gray-500">{field.example}</td>}
                                  {field.required !== undefined && (
                                    <td className="px-3 py-2">
                                      {field.required ? (
                                        <span className="text-green-600 font-medium">Yes</span>
                                      ) : (
                                        <span className="text-gray-400">No</span>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Email Types Table */}
                      {section.content.emailTypes && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="text-left px-3 py-2 font-medium text-gray-700 rounded-tl-lg">Type</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-700">When Sent</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-700 rounded-tr-lg">Content</th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.content.emailTypes.map((email, i) => (
                                <tr key={i} className="border-b border-gray-100">
                                  <td className="px-3 py-2 font-medium text-gray-800">{email.type}</td>
                                  <td className="px-3 py-2 text-gray-600">{email.when}</td>
                                  <td className="px-3 py-2 text-gray-500">{email.content}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Variables */}
                      {section.content.variables && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-700 mb-3">Email Template Variables:</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {section.content.variables.map((v, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <code className="bg-gray-200 px-2 py-0.5 rounded text-xs font-mono text-gray-700">{v.var}</code>
                                <span className="text-gray-500">{v.desc}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Channels */}
                      {section.content.channels && (
                        <div className="grid md:grid-cols-3 gap-3">
                          {section.content.channels.map((ch, i) => (
                            <div key={i} className="bg-white border border-gray-200 rounded-lg p-3">
                              <h5 className="font-medium text-gray-800">{ch.name}</h5>
                              <p className="text-sm text-gray-600">{ch.desc}</p>
                              <p className="text-xs text-gray-400 mt-1">{ch.req}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Open Methods */}
                      {section.content.openMethods && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-700 mb-2">How to Open Message Console:</h4>
                          <ul className="space-y-1 text-sm">
                            {section.content.openMethods.map((method, i) => (
                              <li key={i} className="text-gray-600 flex items-start gap-2">
                                <span className="text-yellow-500">•</span> {method}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Features List */}
                      {section.content.features && (
                        <div className="grid md:grid-cols-2 gap-2">
                          {section.content.features.map((feature, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                              <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
                              {feature}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Actions */}
                      {section.content.actions && (
                        <div className="flex flex-wrap gap-3">
                          {section.content.actions.map((action, i) => (
                            <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                              <span className="text-lg">{action.icon}</span>
                              <div>
                                <p className="text-sm font-medium text-gray-800">{action.name}</p>
                                <p className="text-xs text-gray-500">{action.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* After Booking */}
                      {section.content.afterBooking && (
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <h4 className="font-medium text-green-800 mb-2">After Booking:</h4>
                          <ul className="space-y-1 text-sm">
                            {section.content.afterBooking.map((item, i) => (
                              <li key={i} className="text-green-700 flex items-start gap-2">
                                <span className="text-green-400">✓</span> {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Multi Customer Info */}
                      {section.content.multiCustomer && Array.isArray(section.content.multiCustomer) && (
                        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                          <h4 className="font-medium text-orange-800 mb-2">Multi-Customer Bookings:</h4>
                          <ul className="space-y-1 text-sm">
                            {section.content.multiCustomer.map((item, i) => (
                              <li key={i} className="text-orange-700 flex items-start gap-2">
                                <span className="text-orange-400">•</span> {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Tip */}
                      {section.content.tip && (
                        <div className="flex items-start gap-3 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                          <span className="text-lg">💡</span>
                          <p className="text-sm text-yellow-800"><strong>Tip:</strong> {section.content.tip}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Help Footer */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6 text-center">
          <h3 className="font-semibold text-gray-800 mb-2">Need More Help?</h3>
          <p className="text-gray-600 text-sm mb-4">
            Click the Support icon (?) in the header for live chat assistance.
          </p>
          <button
            onClick={handleDownload}
            disabled={isGeneratingPdf}
            className="inline-flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {isGeneratingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {isGeneratingPdf ? 'Generating...' : 'Download Complete PDF Guide'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserGuidePage;
